import {
  generateCreativeTemplate,
  getCreativeTemplateSignedUrl,
  uploadCreativeTemplateAsset,
} from "@/app/_api/creativeTemplates";
import type { InteractiveImageGenerate } from "@ui/components/demos/InteractiveImage";

/**
 * Builds the {@link InteractiveImageGenerate} callback for InteractiveImage create mode against the
 * legacy anonymous creative-templates API. Pure and React-free so it can be unit-tested.
 *
 * The single `asset_id` slot is filled by precedence: a prior result chained by its `assetId` (no
 * re-upload) when editing, else the uploaded reference file on a first generation. A source picks
 * the EDITION template, none picks CREATION; the result carries `asset_id` so edits can chain on.
 */
export function buildOnGenerate({
  creationTemplateSlug,
  editionTemplateSlug,
}: {
  creationTemplateSlug: string;
  editionTemplateSlug: string;
}): InteractiveImageGenerate {
  return async ({ prompt, attachments, baseImageAssetId, signal }) => {
    let sourceAssetId = baseImageAssetId;
    if (!sourceAssetId && attachments[0]) {
      const upload = await uploadCreativeTemplateAsset(attachments[0], signal);
      sourceAssetId = upload.asset_id;
    }

    const templateSlug = sourceAssetId ? editionTemplateSlug : creationTemplateSlug;
    const generated = await generateCreativeTemplate({
      templateSlug,
      assetId: sourceAssetId,
      userInput: prompt,
      signal,
    });
    if (!generated.asset_id) {
      throw new Error("[InteractiveImageLive] generate returned no asset_id");
    }

    const signed = await getCreativeTemplateSignedUrl(generated.asset_id, signal);
    return { imageUrl: signed.url, alt: prompt, assetId: generated.asset_id };
  };
}
